import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import Loader from '../components/Loader';
import { getURL } from '../utils/api';
import { MdAttachFile } from 'react-icons/md';

const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding: 24px 16px 48px;

  @media (max-width: 768px) {
    padding: 14px 10px 24px;
  }
`;

const Container = styled.div`
  max-width: 980px;
  margin: 0 auto;
`;

const Header = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px;
  margin-bottom: 16px;
  position: sticky;
  top: 12px;
  z-index: 15;

  @media (max-width: 768px) {
    position: static;
    padding: 14px;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
`;

const HeaderTopActions = styled.div`
  display: none;

  @media (min-width: 992px) {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const DesktopHeaderToggle = styled.button`
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #1e3a8a;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  margin-top: 2px;
  display: none;

  @media (min-width: 992px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const HeaderBody = styled.div`
  display: ${(p) => (p.$hidden ? 'none' : 'block')};

  @media (max-width: 991px) {
    display: block !important;
  }
`;

const HeaderCompactPalette = styled.div`
  display: none;

  @media (min-width: 992px) {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
  }
`;

const CompactPaletteButton = styled.a`
  min-width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid ${(p) => (p.$answered ? '#86efac' : '#cbd5e1')};
  background: ${(p) => (p.$answered ? '#f0fdf4' : '#ffffff')};
  color: ${(p) => (p.$answered ? '#166534' : '#334155')};

  &:hover {
    background: ${(p) => (p.$answered ? '#ecfdf5' : '#f8fafc')};
  }

  &:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: 1px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  color: #0f172a;
`;

const SubTitle = styled.p`
  margin: 6px 0 0;
  color: #64748b;
  font-size: 14px;
`;

const TopRow = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const BadgeGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 768px) {
    width: 100%;
    gap: 8px;
  }
`;

const Badge = styled.div`
  padding: 6px 10px;
  border-radius: 10px;
  font-size: 12px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  color: #334155;

  @media (max-width: 768px) {
    flex: 1 1 calc(50% - 8px);
    text-align: center;
  }
`;

const Timer = styled.div`
  padding: 8px 12px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 14px;
  border: 1px solid ${(p) => (p.$danger ? '#fecaca' : '#bfdbfe')};
  background: ${(p) => (p.$danger ? '#fef2f2' : '#eff6ff')};
  color: ${(p) => (p.$danger ? '#b91c1c' : '#1d4ed8')};

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
  }
`;

const HeaderMiniTimer = styled(Timer)`
  width: auto;
  white-space: nowrap;
`;

const ProgressWrap = styled.div`
  margin-top: 12px;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  width: ${(p) => `${p.$width || 0}%`};
  background: linear-gradient(90deg, #2563eb, #0ea5e9);
  transition: width 0.2s ease;
`;

const ProgressText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #475569;
`;

const StatusBanner = styled.div`
  margin-top: 12px;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  border: 1px solid ${(p) => (p.$ready ? '#bbf7d0' : '#fde68a')};
  background: ${(p) => (p.$ready ? '#f0fdf4' : '#fffbeb')};
  color: ${(p) => (p.$ready ? '#166534' : '#92400e')};
`;

const PaletteWrap = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 6px;
  }
`;

const PaletteButton = styled.a`
  min-width: 34px;
  height: 34px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid ${(p) => (p.$answered ? '#86efac' : '#cbd5e1')};
  background: ${(p) => (p.$answered ? '#f0fdf4' : '#ffffff')};
  color: ${(p) => (p.$answered ? '#166534' : '#334155')};

  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
    height: 32px;
  }
`;

const Description = styled.p`
  margin: 10px 0 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.6;
`;

const AttachmentActions = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const AttachmentActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  border: 1px solid #cbd5e1;
  color: #1e3a8a;
  background: #ffffff;
  border-radius: 8px;
  padding: 6px 10px;
  text-decoration: none;
`;

const QuestionCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 14px;
  scroll-margin-top: 300px;

  @media (max-width: 768px) {
    padding: 12px;
    scroll-margin-top: 120px;
  }
`;

const QuestionNo = styled.div`
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
`;

const QuestionText = styled.div`
  color: #1f2937;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const QuestionImage = styled.img`
  margin-top: 10px;
  max-width: 100%;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
`;

const OptionList = styled.div`
  margin-top: 12px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid ${(p) => (p.$selected ? '#93c5fd' : '#e5e7eb')};
  background: ${(p) => (p.$selected ? '#eff6ff' : '#ffffff')};
  border-radius: 10px;
  padding: 10px;
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
`;

const OptionText = styled.div`
  color: #1f2937;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const FooterBar = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 14px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ModalCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 28px 24px 24px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
`;

const ModalIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #d1fae5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin: 0 auto 16px;
`;

const ModalTitle = styled.h2`
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  text-align: center;
`;

const ModalSubtitle = styled.p`
  margin: 0 0 20px;
  font-size: 13px;
  color: #64748b;
  text-align: center;
`;

const ScoreBox = styled.div`
  background: linear-gradient(135deg, #1d4ed8, #0ea5e9);
  border-radius: 14px;
  padding: 18px;
  text-align: center;
  color: #ffffff;
  margin-bottom: 16px;
`;

const ScoreNumber = styled.div`
  font-size: 36px;
  font-weight: 800;
  line-height: 1;
`;

const ScoreMax = styled.div`
  font-size: 14px;
  opacity: 0.8;
  margin-top: 4px;
`;

const ScoreCalc = styled.div`
  font-size: 12px;
  opacity: 0.7;
  margin-top: 6px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 10px 6px;
  text-align: center;
  background: #f8fafc;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${(p) => p.$color || '#0f172a'};
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
`;

const Button = styled.button`
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? '#e2e8f0' : '#2563eb')};
  color: ${(p) => (p.$secondary ? '#0f172a' : '#ffffff')};
  opacity: ${(p) => (p.disabled ? 0.6 : 1)};
`;

const InlineError = styled.div`
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 14px;
  font-size: 14px;
`;

function buildImageHref(imagePath) {
  if (!imagePath) return null;
  const base = getURL();
  const normalised = imagePath.startsWith('/api/') ? imagePath.slice(4) : imagePath;
  return `${base}${normalised}`;
}

function buildAttachmentHref(attachmentPath, options = {}) {
  if (!attachmentPath) return null;
  const raw = String(attachmentPath).trim();
  const lowered = raw.toLowerCase();
  if (!raw || lowered === 'null' || lowered === 'undefined') return null;
  const base = getURL();
  const normalised = raw.startsWith('/api/') ? raw.slice(4) : raw;
  if (!options?.download) return `${base}${normalised}`;
  const params = new URLSearchParams({ download: '1' });
  if (options.name) params.set('name', options.name);
  return `${base}${normalised}?${params.toString()}`;
}

function secondsToClock(totalSeconds) {
  const safe = Math.max(0, Number(totalSeconds) || 0);
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function formatMarks(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
}

function toSentenceCase(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const lower = text.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export default function ExamPaper() {
  const navigate = useNavigate();
  const { examId } = useParams();
  const [searchParams] = useSearchParams();
  const batchId = Number(searchParams.get('batchId'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [resultPayload, setResultPayload] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const autoSubmitRef = useRef(false);

  const loadPaper = useCallback(async () => {
    if (!Number.isFinite(batchId) || batchId <= 0) {
      setError('Batch context is missing. Please start the exam from My Exams page.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const { data } = await axiosInstance.get(`/students/my-exams/${examId}/paper`, {
        params: { batchId },
      });
      setExam(data.exam || null);
      setQuestions(Array.isArray(data.questions) ? data.questions : []);
      setTimerSeconds(Math.max(0, Number(data?.exam?.minutes || 0) * 60));
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load exam paper');
    } finally {
      setLoading(false);
    }
  }, [batchId, examId]);

  useEffect(() => {
    loadPaper();
  }, [loadPaper]);

  useEffect(() => {
    if (!exam || resultPayload || submitting) return undefined;
    if (timerSeconds <= 0) return undefined;

    const id = window.setInterval(() => {
      setTimerSeconds((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => window.clearInterval(id);
  }, [exam, resultPayload, submitting, timerSeconds]);

  const handleSubmit = useCallback(async (isAuto = false) => {
    if (!exam || submitting || resultPayload) return;

    try {
      setSubmitting(true);
      setError('');

      const payloadAnswers = Object.entries(answers).map(([questionId, selectedAnswer]) => ({
        questionId: Number(questionId),
        selectedAnswer: Number(selectedAnswer),
      }));

      const { data } = await axiosInstance.post(`/students/my-exams/${examId}/submit`, {
        batchId,
        answers: payloadAnswers,
      });

      setResultPayload(data);
      setShowResultModal(true);
      if (isAuto) {
        setError('Time is up. Your exam was auto-submitted.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  }, [answers, batchId, exam, examId, resultPayload, submitting]);

  useEffect(() => {
    if (!exam || resultPayload || submitting) return;
    if (timerSeconds > 0) return;
    if (autoSubmitRef.current) return;

    autoSubmitRef.current = true;
    handleSubmit(true);
  }, [exam, handleSubmit, resultPayload, submitting, timerSeconds]);

  const answeredCount = useMemo(
    () => Object.keys(answers).length,
    [answers]
  );

  const totalQuestions = questions.length;
  const unansweredCount = Math.max(totalQuestions - answeredCount, 0);
  const allAnswered = totalQuestions > 0 && unansweredCount === 0;
  const completionPercent = totalQuestions > 0
    ? Math.round((answeredCount / totalQuestions) * 100)
    : 0;
  const attachmentHref = buildAttachmentHref(exam?.attachmentPath);
  const attachmentFileName = (() => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const datePart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const timePart = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    return `${toSentenceCase(exam?.examName || 'exam')}_${datePart}_${timePart}`;
  })();
  const attachmentDownloadHref = buildAttachmentHref(exam?.attachmentPath, { download: true, name: attachmentFileName });

  const onSelectAnswer = (questionId, value) => {
    if (resultPayload) return;
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const reviewByQuestionId = useMemo(() => {
    if (!resultPayload?.review) return new Map();
    return new Map(resultPayload.review.map((r) => [r.questionId, r]));
  }, [resultPayload]);

  const handleReAttempt = () => {
    if (!exam) return;
    autoSubmitRef.current = false;
    setAnswers({});
    setResultPayload(null);
    setShowResultModal(false);
    setError('');
    setSubmitting(false);
    setTimerSeconds(Math.max(0, Number(exam.minutes || 0) * 60));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <Page>
        <Container>
          <Loader />
        </Container>
      </Page>
    );
  }

  return (
    <Page>
      <Container>
        {error && <InlineError>{error}</InlineError>}

        {showResultModal && resultPayload?.result && (
          <ModalOverlay>
            <ModalCard>
              <ModalIcon>🎉</ModalIcon>
              <ModalTitle>Exam Submitted!</ModalTitle>
              <ModalSubtitle>
                {toSentenceCase(resultPayload.exam?.examName || exam?.examName || 'Exam')}
              </ModalSubtitle>

              <ScoreBox>
                <ScoreNumber>{formatMarks(resultPayload.result.obtainedMarks)}</ScoreNumber>
                <ScoreMax>out of {formatMarks(resultPayload.result.maxMarks)} marks</ScoreMax>
                <ScoreCalc>
                  {resultPayload.result.correctCount} correct × {formatMarks(resultPayload.exam?.marksForMcq || 0)} marks each
                </ScoreCalc>
              </ScoreBox>

              <StatsRow>
                <StatCard>
                  <StatValue $color="#166534">{resultPayload.result.correctCount}</StatValue>
                  <StatLabel>Correct</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue $color="#b91c1c">{resultPayload.result.wrongCount}</StatValue>
                  <StatLabel>Wrong</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue $color="#92400e">{resultPayload.result.unansweredCount}</StatValue>
                  <StatLabel>Unanswered</StatLabel>
                </StatCard>
              </StatsRow>

              <ModalActions>
                <Button onClick={() => { setShowResultModal(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                  Review Answers
                </Button>
                <Button $secondary onClick={handleReAttempt}>Re-attempt Exam</Button>
                <Button $secondary onClick={() => navigate('/app/my-exam')}>Back To My Exams</Button>
              </ModalActions>
            </ModalCard>
          </ModalOverlay>
        )}

        <Header>
          <HeaderTop>
            <div>
              <Title>{toSentenceCase(exam?.examName || 'Exam paper')}</Title>
              <SubTitle>Digital exam paper. Answer all questions and submit before time ends.</SubTitle>
            </div>
            <HeaderTopActions>
              {!isHeaderExpanded && !resultPayload && (
                <HeaderMiniTimer $danger={timerSeconds <= 60}>
                  Time Left: {secondsToClock(timerSeconds)}
                </HeaderMiniTimer>
              )}
              <DesktopHeaderToggle
                type="button"
                onClick={() => setIsHeaderExpanded((prev) => !prev)}
              >
                {isHeaderExpanded ? 'Hide Details' : 'View Details'}
              </DesktopHeaderToggle>
            </HeaderTopActions>
          </HeaderTop>

          {!isHeaderExpanded && !resultPayload && totalQuestions > 0 && (
            <HeaderCompactPalette>
              {questions.map((q, idx) => (
                <CompactPaletteButton
                  key={`compact-${q.questionId}`}
                  href={`#question-${q.questionId}`}
                  $answered={Boolean(answers[q.questionId])}
                >
                  {idx + 1}
                </CompactPaletteButton>
              ))}
            </HeaderCompactPalette>
          )}

          <HeaderBody $hidden={!isHeaderExpanded}>
            {exam?.description && <Description>{exam.description}</Description>}
            {attachmentHref && (
              <AttachmentActions>
                <AttachmentActionLink href={attachmentHref} target="_blank" rel="noopener noreferrer">
                  <MdAttachFile size={14} />
                  View Attachment
                </AttachmentActionLink>
                <AttachmentActionLink href={attachmentDownloadHref} download={attachmentFileName}>
                  <MdAttachFile size={14} />
                  Download Attachment
                </AttachmentActionLink>
              </AttachmentActions>
            )}

            <TopRow>
              <BadgeGroup>
                <Badge>Total Questions: {questions.length}</Badge>
                <Badge>Marks per MCQ: {formatMarks(exam?.marksForMcq || 0)}</Badge>
                <Badge>Answered: {answeredCount}</Badge>
              </BadgeGroup>
              <Timer $danger={timerSeconds <= 60}>
                Time Left: {secondsToClock(timerSeconds)}
              </Timer>
            </TopRow>

            <ProgressWrap>
              <ProgressTrack>
                <ProgressFill $width={completionPercent} />
              </ProgressTrack>
              <ProgressText>
                Completion: {completionPercent}% ({answeredCount}/{totalQuestions})
              </ProgressText>
            </ProgressWrap>

            {!resultPayload && (
              <StatusBanner $ready={allAnswered}>
                {allAnswered
                  ? 'All questions are answered. Submit now to see marks and correct answers.'
                  : `${unansweredCount} question(s) still unanswered. Complete all answers to enable submit.`}
              </StatusBanner>
            )}

            {!resultPayload && totalQuestions > 0 && (
              <PaletteWrap>
                {questions.map((q, idx) => (
                  <PaletteButton
                    key={q.questionId}
                    href={`#question-${q.questionId}`}
                    $answered={Boolean(answers[q.questionId])}
                  >
                    {idx + 1}
                  </PaletteButton>
                ))}
              </PaletteWrap>
            )}
          </HeaderBody>
        </Header>

        {questions.map((q, index) => {
          const selected = answers[q.questionId] || null;
          const review = reviewByQuestionId.get(q.questionId) || null;
          const questionImageHref = buildImageHref(q.questionImage);

          return (
            <QuestionCard key={q.questionId} id={`question-${q.questionId}`}>
              <QuestionNo>Question {index + 1}</QuestionNo>
              <QuestionText>{q.question}</QuestionText>
              {questionImageHref && <QuestionImage src={questionImageHref} alt={`Question ${index + 1}`} />}

              <OptionList>
                {q.options.map((opt) => (
                  <OptionLabel
                    key={opt.number}
                    $selected={Number(selected) === opt.number}
                    $disabled={!!resultPayload}
                  >
                    <input
                      type="radio"
                      name={`q_${q.questionId}`}
                      value={opt.number}
                      checked={Number(selected) === opt.number}
                      disabled={!!resultPayload}
                      onChange={() => onSelectAnswer(q.questionId, opt.number)}
                    />
                    <OptionText>{opt.text}</OptionText>
                  </OptionLabel>
                ))}
              </OptionList>

              {review && (
                <div style={{ marginTop: 10, fontSize: 13, color: review.isCorrect ? '#166534' : '#b91c1c' }}>
                  {review.isCorrect ? 'Correct answer.' : 'Incorrect answer.'} Correct option: {review.correctAnswer} - {review.correctAnswerText}
                </div>
              )}
            </QuestionCard>
          );
        })}

        <FooterBar>
          <div style={{ color: '#475569', fontSize: 13 }}>
            {resultPayload
              ? 'Review mode enabled. Answers are locked.'
              : allAnswered
                ? 'Ready to submit. Marks and correct answers will be shown after submission.'
                : 'Answer all questions to enable submit.'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button $secondary onClick={() => navigate('/app/my-exam')}>Back To My Exams</Button>
            {!resultPayload && (
              <Button
                onClick={() => handleSubmit(false)}
                disabled={submitting || !exam || !allAnswered}
              >
                {submitting ? 'Submitting...' : 'Submit And Show Marks'}
              </Button>
            )}
          </div>
        </FooterBar>
      </Container>
    </Page>
  );
}
